import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2Icon, ChevronDownIcon, LockIcon } from 'lucide-react';
import { EMPTY_LEAD, Lead, MATERIAL_OPTIONS, MaterialRequerido } from '../types/lead';
import { trackLeadSubmit } from '../utils/analytics';
import { WhatsAppButton } from './WhatsAppButton';

/* Todos los campos comparten alto, ancho y estilo para verse uniformes en cualquier pantalla */
const inputClass = 'mt-1.5 block h-12 w-full min-w-0 max-w-full appearance-none rounded-md border border-steel-200 bg-white px-3.5 text-left text-navy-900 placeholder:text-steel-400 focus:border-navy-600 focus:outline-none focus:ring-2 focus:ring-navy-600/20 [&::-webkit-date-and-time-value]:text-left';
const labelClass = 'block text-sm font-semibold text-navy-800';
export function LeadForm() {
  const [lead, setLead] = useState<Lead>(EMPTY_LEAD);
  const [submitted, setSubmitted] = useState(false);
  const update = <K extends keyof Lead,>(key: K, value: Lead[K]) => setLead(prev => ({
    ...prev,
    [key]: value
  }));
  const successMessage = `Hola OA LOSAS, acabo de llenar el formulario de cotización.

Nombre: ${lead.nombre}
Material: ${lead.material}
Cantidad o m²: ${lead.cantidad}
Ciudad de entrega: ${lead.ciudad}
Fecha en que lo necesito: ${lead.fecha || 'Por definir'}`;
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    trackLeadSubmit({
      source: 'formulario',
      material: lead.material,
      ciudad: lead.ciudad,
      cantidad: lead.cantidad
    });
    setSubmitted(true);
  };
  return <section id="formulario" className="scroll-mt-16 bg-navy-900 py-10 text-white sm:py-16" data-id="element-85" data-mp="23bl3w6bjj74l">
      <div className="mx-auto grid max-w-content grid-cols-1 gap-6 px-4 sm:px-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-14" data-id="element-86" data-mp="1okc81fh1opj0">
        <div className="min-w-0" data-id="element-87" data-mp="2h9jfx52e2rgx">
          <h2 className="font-display text-[1.75rem] font-bold leading-tight sm:text-4xl" data-id="element-88" data-mp="2h8ht63c0ggjd">
            Recibe la lista de precios 2026, ficha técnica y disponibilidad
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-steel-200 sm:text-base" data-id="element-89" data-mp="2vw8g794gt8od">
            Déjanos tus datos y te lo enviamos a tu correo. Si tienes prisa, WhatsApp es el camino más rápido.
          </p>
          <p className="mt-4 hidden items-start gap-2.5 text-sm text-steel-400 lg:flex" data-id="element-90" data-mp="2lwrbov0y90tz">
            <LockIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent-500" aria-hidden="true" data-id="element-91" data-mp="2hwyw3yd51uf4" />
            Solo usamos tus datos para cotizar y dar seguimiento. No los compartimos y puedes darte de baja cuando
            quieras.
          </p>
        </div>

        <div className="min-w-0 rounded-xl bg-white p-4 text-navy-900 sm:p-7" data-id="element-92" data-mp="284ksf02i2dqb">
          {submitted ? <div role="status" data-id="element-93" data-mp="1y882mda2sk0u">
              <CheckCircle2Icon className="h-10 w-10 text-signal-600" aria-hidden="true" data-id="element-94" data-mp="1y8zogwdvf3dx" />
              <h3 className="mt-3 font-display text-2xl font-bold leading-tight sm:text-3xl" data-id="element-95" data-mp="1u74phyjex23b">
                Gracias. Ya recibimos tus datos.
              </h3>
              <p className="mt-2 text-[15px] text-steel-600" data-id="element-96" data-mp="1p9w1vifhw5uo">Para acelerar tu cotización, escríbenos ahora por WhatsApp.</p>
              <WhatsAppButton message={successMessage} label="Continuar por WhatsApp" location="form_success" intent="post_formulario" size="lg" fullWidth className="mt-5" data-id="element-97" data-mp="9obzkfdmpffg" />
              <Link to="/gracias" className="mt-4 inline-block text-sm font-medium text-navy-700 underline underline-offset-4 transition-colors duration-150 ease-out hover:text-accent-600" data-id="element-98" data-mp="3i4nlagclofgk">
                Ver el siguiente paso de tu cotización
              </Link>
            </div> : <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-99" data-mp="3vxd64hge7ec3">
              <div className="min-w-0" data-id="element-100" data-mp="2yfmtmkec9rli">
                <label className={labelClass} htmlFor="nombre" data-id="element-101" data-mp="33fhnmwteuxas">
                  Nombre
                </label>
                <input id="nombre" required autoComplete="name" placeholder="Tu nombre" value={lead.nombre} onChange={e => update('nombre', e.target.value)} className={inputClass} data-id="element-102" data-mp="19wipo7ap72v5" />
              </div>

              <div className="min-w-0" data-id="element-103" data-mp="1dwswfm6bdxb2">
                <label className={labelClass} htmlFor="email" data-id="element-104" data-mp="1sn9ucvjqq537">
                  Email
                </label>
                <input id="email" type="email" inputMode="email" autoCapitalize="none" required autoComplete="email" placeholder="tu@correo.com" value={lead.email} onChange={e => update('email', e.target.value)} className={inputClass} data-id="element-105" data-mp="1nofd3y1f8ol7" />
              </div>

              <div className="min-w-0" data-id="element-106" data-mp="3a51quw0lgbp">
                <label className={labelClass} htmlFor="telefono" data-id="element-107" data-mp="3pa1d12je1zk7">
                  Teléfono / WhatsApp
                </label>
                <input id="telefono" type="tel" inputMode="tel" required autoComplete="tel" placeholder="10 dígitos" value={lead.telefono} onChange={e => update('telefono', e.target.value)} className={inputClass} data-id="element-108" data-mp="3tabf444285ts" />
              </div>

              <div className="min-w-0" data-id="element-109" data-mp="3aloga3ch8con">
                <label className={labelClass} htmlFor="ciudad" data-id="element-110" data-mp="2wl3tkw3fxwpv">
                  Ciudad de entrega
                </label>
                <input id="ciudad" required autoComplete="address-level2" placeholder="Ej. León" value={lead.ciudad} onChange={e => update('ciudad', e.target.value)} className={inputClass} data-id="element-111" data-mp="31jovczyccogf" />
              </div>

              <div className="min-w-0" data-id="element-112" data-mp="xc2i7ucfz5kj">
                <label className={labelClass} htmlFor="material" data-id="element-113" data-mp="tc246at98we0">
                  Material
                </label>
                <div className="relative" data-id="element-114" data-mp="oejuujbbsf5h">
                  <select id="material" value={lead.material} onChange={e => update('material', e.target.value as MaterialRequerido)} className={`${inputClass} pr-10`} data-id="element-115" data-mp="ofl7utbwjbpu">
                    {MATERIAL_OPTIONS.map(option => <option key={option} value={option} data-id="element-116" data-mp="17c37dwwzsfwn">
                        {option}
                      </option>)}
                  </select>
                  <ChevronDownIcon className="pointer-events-none absolute right-3 top-[calc(50%+3px)] h-4 w-4 -translate-y-1/2 text-steel-600" aria-hidden="true" data-id="element-117" data-mp="12b5mvdoif9rz" />
                </div>
              </div>

              <div className="min-w-0" data-id="element-118" data-mp="22edhmc691d4h">
                <label className={labelClass} htmlFor="cantidad" data-id="element-119" data-mp="22fg9onfb5llz">
                  Piezas o m² de losa
                </label>
                <input id="cantidad" required placeholder="Ej. 120 m² o 65 piezas" value={lead.cantidad} onChange={e => update('cantidad', e.target.value)} className={inputClass} data-id="element-120" data-mp="2caqa56q25kxn" />
              </div>

              <div className="min-w-0 sm:col-span-2" data-id="element-121" data-mp="2q9pzkglea3uv">
                <label className={labelClass} htmlFor="fecha" data-id="element-122" data-mp="2v1rbg1enhskd">
                  ¿Para cuándo lo necesitas?
                </label>
                <input id="fecha" type="date" value={lead.fecha} onChange={e => update('fecha', e.target.value)} className={inputClass} data-id="element-123" data-mp="2zzspg7x7z1oz" />
              </div>

              <label htmlFor="consentimiento" className="flex items-start gap-3 text-sm text-steel-600 sm:col-span-2" data-id="element-124" data-mp="3uch37jww1978">
                <input id="consentimiento" type="checkbox" required checked={lead.consentimiento} onChange={e => update('consentimiento', e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-accent-500" data-id="element-125" data-mp="3qbnckis29gan" />
                <span data-id="element-126" data-mp="3bm9217cr0sto">Acepto recibir seguimiento por WhatsApp y email sobre mi cotización.</span>
              </label>

              <button type="submit" className="min-h-[52px] rounded-md bg-accent-500 px-6 text-base font-semibold text-white transition-[background-color,transform] duration-150 ease-out hover:bg-accent-600 active:scale-[0.99] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500 sm:col-span-2" data-id="element-127" data-mp="36l1tv76p1rmh">
                Enviar datos y cotizar
              </button>

              <p className="flex items-start gap-2 text-xs text-steel-600 sm:col-span-2 lg:hidden" data-id="element-128" data-mp="13dkqfn4rb1wv">
                <LockIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent-500" aria-hidden="true" data-id="element-129" data-mp="185um7jsjyw72" />
                Solo usamos tus datos para cotizar. No los compartimos.
              </p>
            </form>}
        </div>
      </div>
    </section>;
}